import React, { Suspense, lazy } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider } from '@/contexts/AuthContext';
import { FavoritesProvider } from '@/contexts/FavoritesContext';
import { ComparisonProvider } from '@/contexts/ComparisonContext';
import { ContentProvider } from '@/contexts/ContentContext';
import { SubscriptionProvider } from '@/contexts/SubscriptionContext';
import { LeadsProvider } from '@/contexts/LeadsContext';
import { ThemeProvider } from '@/contexts/ThemeContext';

import PublicLayout from '@/components/layout/PublicLayout';
import DashboardLayout from '@/components/layout/DashboardLayout';
import ProtectedRoute from '@/components/ProtectedRoute';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Loader2 } from 'lucide-react';


import TerminosCondiciones from '@/pages/TerminosCondiciones';
import PoliticaPrivacidad from '@/pages/PoliticaPrivacidad';

// Eager load critical dashboard component
import AgentDashboard from '@/components/dashboard/AgentDashboard';

// Lazy Load Pages
const Home = lazy(() => import('@/pages/Home'));
const Properties = lazy(() => import('@/pages/Properties'));
const PropertyDetail = lazy(() => import('@/pages/PropertyDetail'));
const Services = lazy(() => import('@/pages/Services'));
const ServiceDetail = lazy(() => import('@/pages/ServiceDetail'));
const FinancialCenter = lazy(() => import('@/pages/FinancialCenter'));
const Blog = lazy(() => import('@/pages/Blog'));
const BlogPost = lazy(() => import('@/pages/BlogPost'));
const AboutUs = lazy(() => import('@/pages/AboutUs'));
const Contact = lazy(() => import('@/pages/Contact'));
const AppraisalSystem = lazy(() => import('@/pages/AppraisalSystem'));
const SellProperty = lazy(() => import('@/pages/SellProperty'));
const CreditGuide = lazy(() => import('@/pages/CreditGuide'));
const Favorites = lazy(() => import('@/pages/Favorites'));
const Compare = lazy(() => import('@/pages/Compare'));
const Alerts = lazy(() => import('@/pages/Alerts'));
const FuturaCleaning = lazy(() => import('@/pages/FuturaCleaning'));
const Admin = lazy(() => import('@/pages/Admin'));
const Login = lazy(() => import('@/pages/auth/Login'));
const Register = lazy(() => import('@/pages/auth/Register'));
const DevLogin = lazy(() => import('@/pages/auth/DevLogin'));
const Subscription = lazy(() => import('@/pages/Subscription'));
const MarketingCenter = lazy(() => import('@/pages/MarketingCenter'));
const OperationsCenter = lazy(() => import('@/pages/OperationsCenter'));
const ArchitectCalculator = lazy(() => import('@/pages/ArchitectCalculator'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const CentroInmobiliario = lazy(() => import('@/pages/CentroInmobiliario'));
const AgentProfile = lazy(() => import('@/pages/AgentProfile'));
const UserSettings = lazy(() => import('@/pages/UserSettings'));
const Leads = lazy(() => import('@/pages/Leads'));
const NotFound = lazy(() => import('@/pages/NotFound'));
// const TerminosCondiciones = lazy(() => import('@/pages/TerminosCondiciones'));
// const PoliticaPrivacidad = lazy(() => import('@/pages/PoliticaPrivacidad'));
const DocumentsGen = lazy(() => import('@/pages/DocumentsGen'));
const KnowledgeBase = lazy(() => import('@/pages/KnowledgeBase'));
const AgentCatalog = lazy(() => import('@/pages/AgentCatalog'));
const MyProperties = lazy(() => import('@/pages/MyProperties'));
const AddProperty = lazy(() => import('@/pages/AddProperty'));
const CommercialActivationCenter = lazy(() => import('@/pages/marketing/CommercialActivationCenter'));
const CampaignLanding = lazy(() => import('@/components/marketing/cac/CampaignLanding'));
const SocialGenerator = lazy(() => import('@/components/marketing/SocialGenerator'));
const ZoneMarketReport = lazy(() => import('@/components/tools/ZoneMarketReport'));

const PageLoader = () => (
    <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
    </div>
);

function App() {
    return (
        <AuthProvider>
            <ThemeProvider>
                <Router>
                    <SubscriptionProvider>
                        <LeadsProvider>
                            <ContentProvider>
                                <FavoritesProvider>
                                    <ComparisonProvider>
                                        <Helmet>
                                            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
                                            <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" integrity="sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=" crossOrigin="" />
                                        </Helmet>
                                        <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors duration-300">
                                            <ErrorBoundary>
                                                <Suspense fallback={<PageLoader />}>
                                                    <Routes>
                                                        {/* ... (rutas ocultas por brevedad) */}
                                                        {/* PUBLIC ROUTES (Header + Footer) */}
                                                        <Route element={<PublicLayout />}>
                                                            <Route path="/" element={<Home />} />

                                                            {/* Portal de Inmuebles Global */}
                                                            <Route path="/propiedades" element={<CentroInmobiliario />} />
                                                            <Route path="/catalogo/:userId" element={<AgentCatalog />} />
                                                            <Route path="/agente/:agentId" element={<AgentProfile />} />
                                                            <Route path="/propiedad/:id" element={<PropertyDetail />} />

                                                            {/* Content Pages */}
                                                            <Route path="/servicios" element={<Services />} />
                                                            <Route path="/servicios/:service" element={<ServiceDetail />} />
                                                            <Route path="/blog" element={<Blog />} />
                                                            <Route path="/blog/:slug" element={<BlogPost />} />
                                                            <Route path="/nosotros" element={<AboutUs />} />
                                                            <Route path="/contacto" element={<Contact />} />

                                                            {/* Tools (Public Access) */}
                                                            <Route path="/sistema-de-avaluos" element={<AppraisalSystem />} />
                                                            <Route path="/vender-propiedad" element={<SellProperty />} />
                                                            <Route path="/guia-de-creditos" element={<CreditGuide />} />
                                                            <Route path="/calculadora-arquitectos" element={<ArchitectCalculator />} />
                                                            <Route path="/limpieza" element={<FuturaCleaning />} />

                                                            {/* User Utilities */}
                                                            <Route path="/favoritos" element={<Favorites />} />
                                                            <Route path="/comparar" element={<Compare />} />
                                                            <Route path="/alertas" element={<Alerts />} />
                                                            <Route path="/subscription" element={<Subscription />} />
                                                            <Route path="/planes" element={<Subscription />} />

                                                            {/* Tools (Public Access) */}
                                                            <Route path="/centro-financiero" element={<FinancialCenter />} />
                                                            <Route path="/sistema-de-avaluos" element={<AppraisalSystem />} />

                                                            {/* Auth Pages */}
                                                            <Route path="/login" element={<Login />} />
                                                            <Route path="/register" element={<Register />} />
                                                            <Route path="/forgot-password" element={<ForgotPassword />} />
                                                            <Route path="/reset-password" element={<ResetPassword />} />
                                                            <Route path="/dev-login" element={<DevLogin />} />

                                                            {/* Legal */}
                                                            <Route path="/terminos" element={<TerminosCondiciones />} />
                                                            <Route path="/privacidad" element={<PoliticaPrivacidad />} />

                                                            {/* CAC Public Campaign */}
                                                            <Route path="/c/:token" element={<CampaignLanding />} />
                                                        </Route>

                                                        {/* AGENT DASHBOARD ROUTES */}
                                                        <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                                                            <Route path="/dashboard" element={<AgentDashboard />} />
                                                            <Route path="/properties" element={<MyProperties />} />
                                                            <Route path="/properties/create" element={<AddProperty />} />
                                                            <Route path="/marketing" element={<MarketingCenter />} />
                                                            <Route path="/leads" element={<Leads />} />
                                                            <Route path="/documents" element={<DocumentsGen />} />
                                                            <Route path="/knowledge" element={<ProtectedRoute requirePlan="pro"><KnowledgeBase /></ProtectedRoute>} />
                                                            <Route path="/operaciones" element={<ProtectedRoute requirePlan="pro"><OperationsCenter /></ProtectedRoute>} />
                                                            <Route path="/marketing/activation" element={<CommercialActivationCenter />} />
                                                            <Route path="/settings" element={<UserSettings />} />
                                                            <Route path="/configuracion" element={<UserSettings />} />
                                                            <Route path="/billing" element={<UserSettings />} />
                                                            <Route path="/social-generator" element={<SocialGenerator />} />
                                                            <Route path="/market-report" element={<ZoneMarketReport />} />
                                                        </Route>

                                                        {/* ADMIN ROUTES */}
                                                        <Route path="/admin" element={
                                                            <ProtectedRoute requireAdmin={true}>
                                                                <Admin />
                                                            </ProtectedRoute>
                                                        } />

                                                        {/* Fallback */}
                                                        <Route path="*" element={<NotFound />} />
                                                    </Routes>
                                                </Suspense>
                                            </ErrorBoundary>
                                        </div>
                                        <Toaster />
                                    </ComparisonProvider>
                                </FavoritesProvider>
                            </ContentProvider>
                        </LeadsProvider>
                    </SubscriptionProvider>
                </Router>
            </ThemeProvider>
        </AuthProvider>
    );
}


export default App;
